/**
 * @file product-kind-format.ts
 * @description Форматирование и реестры справочника «Вид товара» и кода склада
 * фасовки (phase19.22, inventory.md W17-1, W17-2, W17-5b, W20, W22).
 *
 * Чистые функции без React — их делят страница справочника, карточка товара,
 * категория и карточка фасовки; покрыты `product-kind-format.test.ts`.
 *
 * Подписи шаблонов этикетки, регламентов и ставок НДС — реестрами: новый
 * шаблон или ставка = запись здесь, а не ветка в разметке.
 */

import type { LabelTemplate, LabelingRegulation, ProductKind } from "./api-product-kinds";

// ─── Код склада (W17-1) ─────────────────────────────────────────────────────

/** `CHB` + 9 цифр + контрольная цифра Луна. */
const INTERNAL_CODE_RE = /^CHB\d{10}$/;

/**
 * Показ кода склада с дефисом перед контрольной цифрой: `CHB0000001237` →
 * `CHB000000123-7`. Дефис — только для глаз: в базе и штрихкоде его нет,
 * пикеры склада нормализуют ввод. Нестандартный код возвращаем как есть.
 */
export function formatInternalCode(code: string | null | undefined): string {
  if (!code) return "";
  const trimmed = code.trim().toUpperCase();
  if (!INTERNAL_CODE_RE.test(trimmed)) return trimmed;
  return `${trimmed.slice(0, -1)}-${trimmed.slice(-1)}`;
}

// ─── GTIN (W17-2) ───────────────────────────────────────────────────────────

const GTIN_LENGTHS = new Set([8, 12, 13, 14]);

/** Контрольная цифра GS1 (mod-10, веса 3/1 справа налево) — как на сервере. */
export function isValidGtin(raw: string): boolean {
  const gtin = raw.trim();
  if (!/^\d+$/.test(gtin) || !GTIN_LENGTHS.has(gtin.length)) return false;
  const digits = gtin.split("").map(Number);
  const check = digits.pop() ?? 0;
  let sum = 0;
  // Справа налево от контрольной: первая цифра с весом 3, затем 1, 3…
  digits.reverse().forEach((d, i) => {
    sum += d * (i % 2 === 0 ? 3 : 1);
  });
  return (10 - (sum % 10)) % 10 === check;
}

/**
 * Текст ошибки GTIN для поля формы; null — всё в порядке или поле пустое
 * (обязательность проверяет форма отдельно — зависит от маркировки ЧЗ).
 */
export function gtinError(raw: string): string | null {
  const gtin = raw.trim();
  if (!gtin) return null;
  if (!/^\d+$/.test(gtin)) return "GTIN — только цифры, без пробелов и букв.";
  if (!GTIN_LENGTHS.has(gtin.length)) {
    return `GTIN — 8, 12, 13 или 14 цифр, сейчас ${gtin.length}.`;
  }
  if (!isValidGtin(gtin)) {
    return "Контрольная цифра GTIN не сходится — сверьте код со штрихкодом на упаковке.";
  }
  return null;
}

// ─── Срок годности: дни → месяцы (W20) ──────────────────────────────────────

/**
 * Месяц срока годности — ровно 30 дней (замечание пользователя 2026-09-26):
 * так пересчёт «дни ↔ месяцы» в паре полей обратим и предсказуем
 * (12 мес. = 360 дн., 45 дн. = 1,5 мес.).
 */
const DAYS_PER_MONTH = 30;

/**
 * Подсказка «≈ 12 мес.» к сроку в днях: хранится в днях, человек думает
 * месяцами (W20). Пусто/не число — пустая строка; меньше полумесяца — «< 1 мес.».
 */
export function shelfLifeMonthsHint(days: number | string | null | undefined): string {
  const n = typeof days === "string" ? Number(days.trim() || NaN) : days;
  if (n === null || n === undefined || !Number.isFinite(n) || n <= 0) return "";
  const months = Math.round(n / DAYS_PER_MONTH);
  return months < 1 ? "< 1 мес." : `≈ ${months} мес.`;
}

/**
 * Поле «дни» → текст поля «месяцы»: `days / 30` до одного знака, запятая
 * как десятичный разделитель, «,0» отбрасывается (45 → «1,5», 360 → «12»).
 * Пусто или не число — пустая строка.
 */
export function daysToMonthsText(days: string | number | null | undefined): string {
  const raw = typeof days === "number" ? String(days) : (days ?? "").trim();
  if (!raw) return "";
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return "";
  const months = Math.round((n / DAYS_PER_MONTH) * 10) / 10;
  return String(months).replace(".", ",");
}

/**
 * Поле «месяцы» → значение поля «дни»: `round(months * 30)`; принимает и
 * запятую, и точку. Пусто или не число — пустая строка.
 */
export function monthsTextToDays(months: string): string {
  const raw = months.trim().replace(",", ".");
  if (!raw) return "";
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return "";
  return String(Math.round(n * DAYS_PER_MONTH));
}

// ─── Реестры подписей ───────────────────────────────────────────────────────

export interface LabelTemplateMeta {
  label: string;
  /** Размер этикетки (W17-5b). */
  size: string;
  /** Короткое пояснение под сегментом. */
  hint: string;
  /** Пропорции пиктограммы (ширина × высота, px). */
  icon: { w: number; h: number };
}

/** Шаблоны складской этикетки — порядок = порядок сегментов. */
export const LABEL_TEMPLATE_META: Record<LabelTemplate, LabelTemplateMeta> = {
  STANDARD: { label: "Стандарт", size: "70×37 мм", hint: "24 на листе", icon: { w: 22, h: 12 } },
  COMPACT: { label: "Компакт", size: "52.5×29.7 мм", hint: "40 на листе", icon: { w: 17, h: 10 } },
  MINI: { label: "Мини", size: "20×20 мм", hint: "только DataMatrix", icon: { w: 10, h: 10 } },
};

export const LABEL_TEMPLATES: LabelTemplate[] = ["STANDARD", "COMPACT", "MINI"];

/** Откуда действующий шаблон фасовки — подпись «Стандарт (из вида)». */
export const LABEL_TEMPLATE_SOURCE_TEXT: Record<"sku" | "kind" | "default", string> = {
  sku: "свой у фасовки",
  kind: "из вида",
  default: "по умолчанию",
};

/** «Стандарт (из вида)» — строка карточки фасовки. */
export function labelTemplateCaption(
  template: LabelTemplate,
  source: "sku" | "kind" | "default",
): string {
  return `${LABEL_TEMPLATE_META[template].label} (${LABEL_TEMPLATE_SOURCE_TEXT[source]})`;
}

/** Регламенты маркировки: короткая подпись для списка и полная — для выбора. */
export const LABELING_REGULATION_META: Record<
  LabelingRegulation,
  { short: string; option: string }
> = {
  TR_TS_022: { short: "ТР ТС 022", option: "ТР ТС 022 — пищевая продукция" },
  TR_TS_009: { short: "ТР ТС 009", option: "ТР ТС 009 — косметика" },
  NONE: { short: "без регламента", option: "Нет" },
  OTHER: { short: "Другой", option: "Другой (указать)" },
};

export const LABELING_REGULATIONS: LabelingRegulation[] = ["TR_TS_022", "TR_TS_009", "OTHER", "NONE"];

/** Предел длины названия «другого» регламента — как на сервере. */
export const REGULATION_NOTE_MAX = 120;

/**
 * Регламент вида для списка и сводки: «ТР ТС 022», «Другой: ТР ТС 027/2012»
 * (решение пользователя 2026-09-26). У OTHER без названия — просто «Другой».
 */
export function regulationLabel(
  kind: Pick<ProductKind, "labelingRegulation" | "labelingRegulationNote">,
): string {
  const short = LABELING_REGULATION_META[kind.labelingRegulation].short;
  const note = kind.labelingRegulationNote?.trim();
  return kind.labelingRegulation === "OTHER" && note ? `${short}: ${note}` : short;
}

/** Раздел техрегламентов на сайте ЕЭК — когда точной страницы нет. */
const EEC_TR_SECTION_URL = "https://eec.eaeunion.org/comission/department/deptexreg/tr/";

/**
 * Пояснение регламента под полем (замечание пользователя 2026-09-26: «что
 * такое Регламент — непонятно»). `url` — официальный текст на сайте ЕЭК;
 * для ТР ТС 009 точная страница не подтверждена — ведём в раздел
 * техрегламентов. Тексты этикетки согласует юрист и технолог.
 */
export const REGULATION_HINTS: Record<LabelingRegulation, { text: string; url: string | null }> = {
  TR_TS_022: {
    text:
      "ТР ТС 022/2011. Пищевая продукция: маркировка потребительской упаковки — состав, масса нетто, " +
      "дата изготовления и срок годности, условия хранения, изготовитель, пищевая ценность, знак ЕАС.",
    url: "https://eec.eaeunion.org/comission/department/deptexreg/tr/PischevkaMarkirovka.php",
  },
  TR_TS_009: {
    text:
      "ТР ТС 009/2011. Парфюмерно-косметическая продукция: назначение, состав (INCI), объём, срок, " +
      "условия хранения, изготовитель, меры предосторожности, знак ЕАС.",
    url: EEC_TR_SECTION_URL,
  },
  NONE: {
    text: "Обязательной маркировки потребительской упаковки нет (декор, упаковка).",
    url: null,
  },
  OTHER: {
    text:
      "Регламент, которого ещё нет в системе: автоматики (шаблон этикетки, проверка полей) по нему " +
      "не будет до следующего релиза, название хранится справочно.",
    url: null,
  },
};

/** Общая строка под полем «Регламент маркировки». */
export const REGULATION_COMMON_HINT =
  "По регламенту система подберёт шаблон потребительской этикетки и проверит обязательные поля " +
  "карточки (следующие фазы). Тексты этикетки — за юристом и технологом.";

/** Подсказка к колонке «Регламент» в списке видов. */
export const REGULATION_COLUMN_HINT =
  "Техрегламент ТС, по которому маркируется потребительская упаковка";

/** Ставки НДС по умолчанию для выбора; пусто — «проставьте вручную». */
export const VAT_RATE_CHOICES: number[] = [0, 10, 20];

/**
 * Опции селекта ставки НДС. Ставка вида, которой нет в списке (задана
 * раньше через API), добавляется, чтобы селект не показал сырое значение.
 */
export function vatRateOptions(current: string): Array<{ value: string; label: string }> {
  const rates = [...VAT_RATE_CHOICES];
  const n = Number(current);
  if (current !== "" && Number.isInteger(n) && !rates.includes(n)) rates.push(n);
  return [
    { value: "", label: "Не задана — проставьте вручную" },
    ...rates.sort((a, b) => a - b).map((r) => ({ value: String(r), label: `${r} %` })),
  ];
}

/**
 * Строка-сводка вида для карточки/таблицы справочника:
 * «Срок: да, 365 дн. (≈ 12 мес.) · Стандарт · ТР ТС 022 · НДС 10 % · возврат ✕».
 */
export function productKindSummary(kind: ProductKind): string[] {
  const expiry = !kind.requiresExpiry
    ? "Срок: не нужен"
    : kind.defaultShelfLifeDays
      ? `Срок: да, ${kind.defaultShelfLifeDays} дн. (${shelfLifeMonthsHint(kind.defaultShelfLifeDays)})`
      : "Срок: да";
  return [
    expiry,
    LABEL_TEMPLATE_META[kind.labelTemplate].label,
    regulationLabel(kind),
    kind.defaultVatRate === null ? "НДС вручную" : `НДС ${kind.defaultVatRate} %`,
    kind.returnToStockAllowed ? "возврат ✓" : "возврат ✕",
  ];
}
